import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TagModule } from 'primeng/tag';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DynamicForm } from '../../../components/form-builder/form-builder.types';
import { getContactFormObject } from './utils/contact-form-object';
import { ContactService } from './contact.service';
import { CompanyContact, Contact } from '@/types';
import { DataTableRowActions, DynamicDataTable } from '../../../components/data-table-builder/data-table.types';
import { getContactDataTableStructure } from './utils/contact-data-table-object';
import { DrawerComponent } from '../../../components/drawer/drawer.component';
import { DrawerObject } from '../../../components/drawer/drawer.types';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { DialogObject } from '../../../components/dialog/dialog.types';
import { CompanyService } from '../company/company.service';
import { ContactRepository } from '../../../store/contact-state/contact-state.repository';
import { MessageService } from 'primeng/api';
import { getContactAddDrawerObject } from './utils/contact-add-drawer-object';
import { getContactDeleteObject } from './utils/contact-delete-dialog-object';
import { CompanyContactExtras, ContactDto } from '@/types/dtos.types';
import { getContactUpdateDrawerObject } from './utils/contact-update-drawer-object copy';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'sm-contact',
  imports: [
    PageHeaderComponent,
    CommonModule,
    ButtonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    TagModule,
    MenuModule,
    InputTextModule,
    FormsModule,
    SelectModule,
    DrawerComponent,
    DataTableBuilderComponent,
    DialogComponent,
  ],
  templateUrl: './contact.component.html',
  styleUrl: './contact.component.scss',
})
export class ContactComponent implements OnInit {
  //services
  contactService = inject(ContactService);
  contactRepository = inject(ContactRepository);
  companyService = inject(CompanyService);
  messageService = inject(MessageService);
  translate = inject(TranslateService);

  //inputs
  @Input() companyId?: number;

  //breadcrumb
  breadcrumb = [
    {
      label: 'settings.contact.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.contact.breadcrumb.contact',
      routerLink: ['/settings', 'contact'],
    },
  ];

  //drawer
  @ViewChild('drawer') drawerComponent!: DrawerComponent;
  contactDrawer?: DrawerObject;

  //dialog
  @ViewChild('dialog') dialogComponent!: DialogComponent;
  contactDialog?: DialogObject;

  //form-object
  formObject: DynamicForm = getContactFormObject({
    store: this.contactRepository,
  });

  //data-table
  //row-actions
  rowActions?: DataTableRowActions;
  //data
  data: Contact[] | CompanyContact[] = [];
  //data-table-object
  dataTableObject?: DynamicDataTable;
  loading = false;

  ngOnInit(): void {
    this.loadData();
    this.rowActions = {
      editAction: () => {
        this.formObject = getContactFormObject({
          store: this.contactRepository,
        });
        this.contactDrawer = getContactUpdateDrawerObject({
          store: this.contactRepository,
          formObject: this.formObject,
          action: () =>
            this.updateContact(
              this.companyId
                ? this.contactRepository.getNested('companyResponse', 'contact', 'id')
                : this.contactRepository.getNested('response', 'id'),
              this.contactRepository.getNested('dto'),
              this.contactRepository.getNested('companyContactExtraDto')
            ),
          toggleVisibility: () => this.drawerComponent.toggleVisible(),
        });
        this.drawerComponent.toggleVisible();
      },
      deleteAction: () => {
        this.contactDialog = getContactDeleteObject({
          t: this.translate,
          store: this.contactRepository,
          representation: this.contactRepository.getNested('response', 'nom')
            ? this.contactRepository.getNested('companyResponse', 'contact', 'nom')
            : this.contactRepository.getNested('response', 'nom'),
          action: () => {
            this.deleteContact(
              this.companyId
                ? this.contactRepository.getNested('companyResponse', 'contact', 'id')
                : this.contactRepository.getNested('response', 'id')
            );
          },
          toggleVisiblity: () => this.dialogComponent.toggleVisible(),
        });
        this.dialogComponent.toggleVisible();
      },
    };
    this.dataTableObject = getContactDataTableStructure({
      companyId: this.companyId,
      store: this.contactRepository,
      createAction: () => {
        this.contactDrawer = getContactAddDrawerObject({
          store: this.contactRepository,
          formObject: this.formObject,
          action: () =>
            this.addContact(this.contactRepository.getNested('dto'), this.companyId, this.contactRepository.getNested('companyContactExtraDto')),
          toggleVisibility: () => this.drawerComponent.toggleVisible(),
        });
        this.drawerComponent.toggleVisible();
      },
      rowActions: this.rowActions,
    });
  }

  loadData() {
    this.loading = true;
    const error = () => {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: this.translate.instant('settings.contact.message.error.loading'),
      });
    };
    if (this.companyId) {
      this.companyService.getCompanyContacts(this.companyId).subscribe({
        next: data => {
          this.data = data.content as CompanyContact[];
          this.loading = false;
        },
        error,
      });
    } else {
      this.contactService.getContacts().subscribe({
        next: data => {
          this.data = data.content as Contact[];
          this.loading = false;
        },
        error,
      });
    }
  }

  addContact(contact: ContactDto, companyId?: number, companyContactExtras?: CompanyContactExtras) {
    const next = () => {
      this.drawerComponent.toggleVisible();
      this.loadData();
      this.loading = false;
      this.contactRepository.reset();
      this.messageService.add({
        severity: 'success',
        summary: 'Contact ajouté',
        detail: this.translate.instant('settings.contact.message.success.added'),
      });
    };

    const error = () => {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: this.translate.instant('settings.contact.message.error.added'),
      });
    };

    if (this.companyId) {
      this.companyService
        .addCompanyContact(companyId!, {
          contact,
          societeContactRequest: companyContactExtras!,
        })
        .subscribe({
          next,
          error,
        });
    } else {
      this.contactService.addContact(contact).subscribe({
        next,
        error,
      });
    }
  }

  updateContact(id: number, contact: ContactDto, companyExtras?: CompanyContactExtras) {
    const next = () => {
      this.drawerComponent.toggleVisible();
      this.loadData();
      this.loading = false;
      this.contactRepository.reset();
      this.messageService.add({
        severity: 'success',
        summary: 'Contact modifié',
        detail: this.translate.instant('settings.contact.message.success.edited'),
      });
    };

    const error = () => {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: this.translate.instant('settings.contact.message.error.edited'),
      });
    };

    this.contactService.updateContact(id, contact).subscribe({
      next: this.companyId
        ? () => {
            if (companyExtras)
              this.companyService.updateCompanyContact(this.companyId!, id, companyExtras).subscribe({
                next,
                error,
              });
          }
        : next,
      error,
    });
  }

  deleteContact(id: number) {
    this.contactService.deleteContact(id).subscribe({
      next: () => {
        this.dialogComponent.toggleVisible();
        this.loadData();
        this.loading = false;
        this.contactRepository.reset();
        this.messageService.add({
          severity: 'success',
          summary: 'Contact supprimé',
          detail: this.translate.instant('settings.contact.message.success.deleted'),
        });
      },
      error: () => {
        this.loading = false;
        this.contactRepository.reset();
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.contact.message.error.deleted'),
        });
      },
    });
  }
}
